import { Seo } from "../components/Seo";
import { PageHeader, Reveal, ClipImage } from "../components/Motion";
import { CtaBand } from "../components/CtaBand";
import { TextMarquee } from "../components/Marquee";

const CHAPTERS = [
  {
    id: "who",
    title: "Who we are",
    text: "1818 Brands LLC is a U.S.-based company that sources and distributes branded apparel, footwear and accessories for the off-price retail market. We sit between the companies that make or own merchandise and the retailers who sell it at value prices.",
  },
  {
    id: "trade",
    title: "Experience in international trade",
    text: "Much of what we handle is produced in one country, held in another and sold in a third. Our work depends on understanding how goods move across borders: documentation, customs, freight timing and the expectations of buyers in different markets.",
  },
  {
    id: "closeouts",
    title: "Branded closeouts and excess inventory",
    text: "Our focus is merchandise that has left its primary channel: end-of-season stock, discontinued lines, cancelled orders and production overruns. We assess these lots quickly and find the retailers they suit.",
  },
  {
    id: "network",
    title: "Our sourcing network",
    text: "Opportunities come to us through relationships built over time with manufacturers, brand owners, licensees, suppliers and logistics providers. That network lets us see inventory early and act on it while it is still available.",
  },
  {
    id: "volume",
    title: "Large-volume execution",
    text: "Off-price retail runs on scale. We are set up to handle large transactions from offer to delivery, coordinating payment terms, preparation and shipping so that a large order is handled as carefully as a small one.",
  },
  {
    id: "relationships",
    title: "Long-term relationships",
    text: "We want suppliers and retailers to come back. That means fair pricing, clear communication and doing what we said we would do. Most of our business comes from people we have worked with before.",
  },
];

const PHOTOS = [
  { src: "/img/warehouse-floor.jpg", alt: "1818 Brands warehouse floor with cartons of branded apparel being sorted", testId: "about-photo-floor" },
  { src: "/img/warehouse-pallets.jpg", alt: "1818 Brands warehouse with palletized inventory ready for distribution", testId: "about-photo-pallets" },
];

export default function About() {
  return (
    <>
      <Seo
        title="About 1818 Brands — Off-Price Sourcing & Distribution"
        description="Learn about 1818 Brands LLC: international trade experience, a focus on branded closeouts and excess inventory, and a sourcing network built on long-term relationships."
      />
      <PageHeader
        eyebrow="About"
        title="A company built on relationships across the supply chain."
        intro="We source and distribute branded merchandise for off-price retail. This is how we work."
        testId="about-header"
      />
      <section className="container-x section" data-testid="about-chapters">
        <div className="grid gap-16 lg:grid-cols-12">
          <aside className="lg:col-span-4" data-testid="about-operation-photos">
            <div className="lg:sticky lg:top-28">
              <p className="eyebrow mb-6 text-[var(--ink-soft)]">Our operation</p>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 lg:gap-5">
                {PHOTOS.map((p) => (
                  <figure key={p.src}>
                    <ClipImage src={p.src} alt={p.alt} className="aspect-[4/3] w-full bg-[var(--paper-2)]" imgClassName="saturate-[0.8]" testId={p.testId} />
                  </figure>
                ))}
              </div>
              <p className="mt-5 max-w-xs text-xs leading-relaxed text-[var(--ink-soft)]">
                Inside our warehouse operation.
              </p>
            </div>
          </aside>
          <div className="lg:col-span-7 lg:col-start-6 lg:pl-8">
            {CHAPTERS.map((c, i) => (
              <article key={c.id} className="grid gap-4 sm:grid-cols-[3.5rem_1fr] border-t border-[var(--line)] py-10 lg:py-12 first:border-t-[var(--ink)]" data-testid={`about-${c.id}`}>
                <Reveal>
                  <p className="pt-2 text-xs tabular-nums tracking-[0.12em] text-[var(--ink-soft)]">0{i + 1}</p>
                </Reveal>
                <Reveal delay={0.06}>
                  <h2 className="font-serif-display !font-normal text-2xl lg:text-[1.75rem] leading-[1.2]">{c.title}</h2>
                  <p className="mt-4 max-w-[34rem] body-copy">{c.text}</p>
                </Reveal>
              </article>
            ))}
          </div>
        </div>
      </section>
      <TextMarquee words={["Reliable execution", "Competitive pricing", "Long-term relationships", "International reach"]} />
      <CtaBand />
    </>
  );
}
